import { useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, ArrowUpRight, AlertTriangle, Newspaper, Pin } from 'lucide-react'
import Footer from '@/components/Footer'
import { useLanguage } from '@/localization'
import { useNewsItem, formatDate, itemDate, type NewsItem, type NewsTag } from '@/useNews'
import type { CommunityData } from '@/useCommunity'
import { newsTagLabel } from '../../../shared/localization/core'

/** Visual style per tag chip (light theme). */
const tagStyles: Record<string, string> = {
  general: 'bg-uliunai-red/10 text-uliunai-red ring-uliunai-red/25',
  kf1: 'bg-uliunai-red/10 text-uliunai-red ring-uliunai-red/25',
  kf2: 'bg-stone-200 text-stone-600 ring-stone-300',
  minecraft: 'bg-green-600/10 text-green-700 ring-green-600/25',
}

function tagClass(tag?: NewsTag): string {
  if (!tag) return tagStyles.general
  return tagStyles[tag.toLowerCase()] ?? tagStyles.general
}

/**
 * /news/:id — a single news post in full. Renders title, date, tag badge, and
 * the complete `body` with line breaks preserved (`whitespace-pre-line`), plus a
 * "Back to news" link and (if `link` is set) an external "Source / read more"
 * link. Mirrors the ConductPage / LegalDoc standalone-page chrome.
 */
export default function NewsDetailPage({ social }: { social: CommunityData['social'] }) {
  const { id } = useParams<{ id: string }>()
  const state = useNewsItem(id)
  const { t } = useLanguage()

  // Land at the top when navigating in from a card link.
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [id])

  return (
    <div className="flex min-h-screen flex-col">
      <main className="mx-auto w-full max-w-3xl flex-1 px-6 py-16 sm:py-20">
        <Link
          to="/news"
          className="inline-flex items-center gap-1.5 font-rajdhani text-sm font-semibold text-stone-500 transition-colors hover:text-uliunai-red"
        >
          <ArrowLeft size={16} />
          {t('Back to news')}
        </Link>

        <div className="mt-8">
          {state.status === 'loading' && <DetailSkeleton />}
          {state.status === 'error' && <DetailError />}
          {state.status === 'notfound' && <NotFound />}
          {state.status === 'ready' && <Article item={state.item} />}
        </div>

        <p className="mt-12 text-center font-rajdhani text-sm text-stone-500">
          <Link to="/" className="font-semibold text-uliunai-red hover:underline">
            {t('Back to the hub')}
          </Link>
        </p>
      </main>

      <Footer social={social} />
    </div>
  )
}

function Article({ item }: { item: NewsItem }) {
  const { t } = useLanguage()
  const date = itemDate(item)

  return (
    <article>
      <header>
        <div className="flex flex-wrap items-center gap-3">
          {item.pinned && (
            <span className="inline-flex items-center gap-1 rounded-full bg-uliunai-red px-2.5 py-1 font-rajdhani text-xs font-bold uppercase tracking-wider text-white">
              <Pin size={12} />
              {t('Pinned')}
            </span>
          )}
          {item.tag && (
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-1 font-rajdhani text-xs font-semibold uppercase tracking-wider ring-1 ${tagClass(item.tag)}`}
            >
              {t(newsTagLabel(String(item.tag)))}
            </span>
          )}
          {date && (
            <time dateTime={date} className="font-rajdhani text-sm font-medium text-stone-500">
              {formatDate(date)}
            </time>
          )}
        </div>

        <h1 className="metal-text mt-5 font-orbitron text-3xl font-black leading-tight tracking-tight sm:text-4xl">
          {item.title}
        </h1>
      </header>

      <div className="mt-8 rounded-2xl border border-stone-line bg-white p-7 shadow-card sm:p-9">
        {item.body ? (
          <p className="whitespace-pre-line font-rajdhani text-base leading-relaxed text-stone-700">
            {item.body}
          </p>
        ) : item.excerpt ? (
          <p className="font-rajdhani text-base leading-relaxed text-stone-700">{item.excerpt}</p>
        ) : (
          <p className="font-rajdhani text-base italic text-stone-500">
            {t('No further details.')}
          </p>
        )}

        {item.link && (
          <a
            href={item.link}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-7 inline-flex items-center gap-1.5 rounded-full border border-stone-line bg-parchment px-5 py-2.5 font-rajdhani text-sm font-bold uppercase tracking-wider text-uliunai-red transition-all hover:-translate-y-0.5 hover:border-uliunai-red/30 hover:shadow-card"
          >
            {t('Source / read more')}
            <ArrowUpRight size={16} />
          </a>
        )}
      </div>
    </article>
  )
}

function DetailSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="flex items-center gap-3">
        <div className="h-5 w-16 rounded-full bg-stone-200" />
        <div className="h-3 w-24 rounded bg-stone-200" />
      </div>
      <div className="mt-5 h-9 w-3/4 rounded bg-stone-200" />
      <div className="mt-8 rounded-2xl border border-stone-line bg-white p-9 shadow-card">
        <div className="space-y-3">
          <div className="h-3 w-full rounded bg-stone-200" />
          <div className="h-3 w-11/12 rounded bg-stone-200" />
          <div className="h-3 w-full rounded bg-stone-200" />
          <div className="h-3 w-4/5 rounded bg-stone-200" />
        </div>
      </div>
    </div>
  )
}

function NotFound() {
  const { t } = useLanguage()

  return (
    <div className="rounded-2xl border border-dashed border-stone-300 bg-white/60 px-6 py-16 text-center">
      <Newspaper size={28} className="mx-auto mb-3 text-stone-400" />
      <p className="font-orbitron text-xl font-bold text-stone-800">{t('Post not found')}</p>
      <p className="mt-2 font-rajdhani text-sm text-stone-500">
        {t('This update may have been removed or never existed.')}
      </p>
      <Link
        to="/news"
        className="mt-6 inline-flex items-center gap-1.5 rounded-full border border-stone-line bg-white px-5 py-2.5 font-rajdhani text-sm font-bold uppercase tracking-wider text-uliunai-red shadow-card transition-all hover:-translate-y-0.5 hover:border-uliunai-red/30 hover:shadow-card-hover"
      >
        <ArrowLeft size={16} />
        {t('All news')}
      </Link>
    </div>
  )
}

function DetailError() {
  const { t } = useLanguage()

  return (
    <div className="rounded-2xl border border-uliunai-red/30 bg-uliunai-red/5 px-6 py-16 text-center">
      <AlertTriangle size={28} className="mx-auto mb-3 text-uliunai-red" />
      <p className="font-rajdhani text-lg font-medium text-stone-800">
        {t("Couldn't load this post")}
      </p>
      <p className="mt-1 font-rajdhani text-sm text-stone-500">
        {t('Please try again in a little while.')}
      </p>
    </div>
  )
}
